<template>
  <main class="container w-full max-w-lg my-12 py-12 px-6 border rounded-lg" data-testid="forgot-password-page">
    <div v-if="!isSuccess">
      <h1 class="text-2xl font-semibold text-center mb-6">Reset Your Password</h1>
      <p class="text-center text-sm mb-8">
        Enter the email address associated with your account,<br />
        and we'll send you a link to reset your password.
      </p>

      <form data-testid="forgot-password-form" @submit.prevent="onSubmit">
        <FormField v-slot="{ componentField }" name="email_address" validate-on-blur :validate-on-change="false">
          <FormItem>
            <FormLabel class="font-medium text-sm">Email</FormLabel>
            <FormControl>
              <Input type="email" placeholder="Enter your email" v-bind="componentField" class="border" data-testid="forgot-password-email" />
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>

        <Button type="submit" class="w-full mt-6 mb-8" :disabled="isSubmitting" data-testid="forgot-password-submit"> Send </Button>

        <div class="text-sm text-center">
          <NuxtLink to="/login" data-testid="forgot-password-back-to-login">Back to Log In</NuxtLink>
        </div>
      </form>
    </div>

    <div v-else class="text-center py-8" data-testid="forgot-password-success">
      <h1 class="text-2xl font-semibold mb-6">Check Your Email</h1>
      <p class="text-sm mb-8">If your account exists, you will receive an email with further instructions for resetting your password.</p>
      <Button type="button" variant="link" class="text-sm w-full" data-testid="forgot-password-back-to-reset" @click="isSuccess = false">Back to Reset Password</Button>
    </div>
  </main>
</template>

<script lang="ts" setup>
import { useForm } from 'vee-validate'
import { toTypedSchema } from '@vee-validate/zod'
import * as z from 'zod'
import ZodMessages from '@@/zod_presets/messages'
import * as Sentry from '@sentry/vue'
import { useToast } from '~/components/ui/toast'

const { toast } = useToast()
const isSuccess = ref(false)

const formSchema = toTypedSchema(
  z.object({
    email_address: z.string().email(ZodMessages.email),
  }),
)

const { handleSubmit, isSubmitting } = useForm({
  validationSchema: formSchema,
})

const onSubmit = handleSubmit(async values => {
  try {
    await $fetch('/v2/accounts/recovery', {
      method: 'POST',
      body: values,
      ...useFetchSharedOptions(),
    })
    isSuccess.value = true
  } catch (error) {
    toast({
      title: 'Failed to Send Reset Link',
      description: 'Reset link was not sent. Please try again.',
      variant: 'destructive',
    })
    Sentry.captureException(error)
  }
})
</script>
